export type AnalyticsEvent = 'verse_lookup' | 'moon_lookup' | 'moon_share' | 'verse_share' | 'verse_copy'
  | 'verse_download' | 'friend_lookup' | 'shared_result_visit';
export type EntryPoint = 'homepage' | 'calculator' | 'monthly_page' | 'phase_guide';

interface EventProperties {
  calculator?: 'birthverse' | 'moon_phase';
  entry_point?: EntryPoint;
  method?: string;
}

// Verified BirthVerse web stream in GA4 property 559295875.
// GTM-NDZ67XV7 still owns its separate legacy G-76RT0SLEP4 destination.
export const GA4_MEASUREMENT_ID = 'G-0S5NPVWDKY';
const METHODS = new Set(['native', 'clipboard', 'copy_link', 'twitter', 'facebook', 'linkedin', 'reddit', 'instagram', 'tiktok', 'png']);
const configuredQueues = new WeakSet<unknown[]>();
const isProductionHost = () => window.location.hostname === 'birthverse.com' || window.location.hostname === 'www.birthverse.com';
const pageLocation = () => `${window.location.origin}${window.location.pathname}`;
const pageReferrer = () => {
  try {
    const referrer = new URL(document.referrer);
    return ['https:', 'http:'].includes(referrer.protocol) ? `${referrer.origin}${referrer.pathname}` : '';
  } catch {
    return '';
  }
};

export const initAnalytics = () => {
  if (!isProductionHost()) return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function () {
      // Google tag's command queue uses the documented Arguments object format.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
  const queue = window.dataLayer;
  if (configuredQueues.has(queue)) return;
  const existingLoader = document.querySelector('script[src*="googletagmanager.com/gtm.js"], script[src*="googletagmanager.com/gtag/js"]');
  if (!existingLoader) {
    // Generated daily pages have no GTM bootstrap; load the official runtime there.
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(script);
    const initialized = queue.some(entry => (entry as { [index: number]: unknown } | null)?.[0] === 'js');
    if (!initialized) window.gtag('js', new Date());
  }
  const alreadyQueued = queue.some(entry => {
    const command = entry as { [index: number]: unknown } | null;
    return command?.[0] === 'config' && command[1] === GA4_MEASUREMENT_ID;
  });
  if (!alreadyQueued) {
    // This config owns the initial view; do not add a manual page_view command.
    window.gtag('config', GA4_MEASUREMENT_ID, {
      send_page_view: true,
      page_location: pageLocation(),
      page_referrer: pageReferrer(),
    });
  }
  configuredQueues.add(queue);
};

export const trackEvent = (eventName: AnalyticsEvent, properties: EventProperties = {}) => {
  if (!isProductionHost()) return;
  initAnalytics();
  const safe: EventProperties = {};
  if (properties.calculator === 'birthverse' || properties.calculator === 'moon_phase') safe.calculator = properties.calculator;
  if (['homepage', 'calculator', 'monthly_page', 'phase_guide'].includes(properties.entry_point || '')) safe.entry_point = properties.entry_point;
  if (properties.method && METHODS.has(properties.method)) safe.method = properties.method;

  // The published container has a Google tag, but no GA4 custom-event tags.
  // Send one Google tag command, rather than a second dataLayer event trigger.
  window.gtag('event', eventName, {
    ...safe,
    send_to: GA4_MEASUREMENT_ID,
    page_location: pageLocation(),
    page_referrer: pageReferrer(),
  });
};
